import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { S as SummaryCardHeader } from '../SummaryCardHeader/SummaryCardHeader-D9OGfI5P.js';
import { c as createVisibilityObserver } from '../utils/createVisibilityObserver-C8NVlrp5.js';
import { u as useIntl } from '../../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-voJ4yxcS-DU7lWLPF.js';
import { T as TaktIdProvider } from '../../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { I as zoomIn } from '../../LocalModules/storm-ui-icons/dist/esm/localIcons-9_jhfe7X-DyCR5FH7.js';

const SummaryCardWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40027066__sc-32050l-0"
})(["box-sizing:border-box;display:flex;flex-direction:column;flex-grow:1;flex-basis:100%;align-items:flex-start;width:", ";", ";min-width:", ";padding:", ";background-color:", ";", ""], ({
  $width
}) => $width, ({
  $maxWidth
}) => $maxWidth && css(["max-width:", ""], $maxWidth), ({
  theme,
  $minWidth
}) => $minWidth ? `${$minWidth}` : theme.summaryCard.minWidth, ({
  theme,
  $padding
}) => {
  var _theme$spacing;
  return (_theme$spacing = theme.spacing[$padding]) === null || _theme$spacing === void 0 ? void 0 : _theme$spacing.default;
}, ({
  theme,
  $backgroundColor
}) => theme.summaryCard.backgroundColor[$backgroundColor], ({
  $disabled
}) => $disabled && css(["pointer-events:none;opacity:", ";cursor:not-allowed;"], ({
  theme
}) => theme.summaryCard.disabled.opacity));
const ContentWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40027066__sc-32050l-1"
})(["width:100%;"]);
const SummaryCard = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    cardName,
    title,
    headerLevel = 'h4',
    headerContent,
    content,
    expandedContent,
    expandButtonLabel,
    onExpandToggle,
    padding = 'base',
    minWidth,
    maxWidth,
    width = 'auto',
    backgroundColor = 'default',
    disabled,
    onCardVisible,
    // summaryRowProps
    _setExpandedCard,
    // taktProps
    taktId,
    taktValue,
    ...rest
  } = props;
  const {
    formatMessage
  } = useIntl();

  // Store visibility observer callback to prevent recreating it on every render
  // This ensures the callback is only triggered once when the card becomes visible
  const visibilityObserverRef = useRef(null);

  // Create visibility observer only once when onCardVisible is provided
  if (onCardVisible && !visibilityObserverRef.current) {
    visibilityObserverRef.current = createVisibilityObserver(onCardVisible, cardName);
  }

  // Cleanup observer on unmount
  useEffect(() => () => {
    var _visibilityObserverRe;
    (_visibilityObserverRe = visibilityObserverRef.current) === null || _visibilityObserverRe === void 0 || _visibilityObserverRe.call(visibilityObserverRef, null);
  }, []);

  // Combined ref handler for both visibility tracking and forwarded ref
  const setRef = node => {
    var _visibilityObserverRe2;
    // Trigger visibility observer if available
    (_visibilityObserverRe2 = visibilityObserverRef.current) === null || _visibilityObserverRe2 === void 0 || _visibilityObserverRe2.call(visibilityObserverRef, node);

    // Forward ref to parent component
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = node;
    }
  };
  const handleOnExpandToggle = event => {
    _setExpandedCard === null || _setExpandedCard === void 0 || _setExpandedCard({
      cardProps: props,
      event
    });
    onExpandToggle === null || onExpandToggle === void 0 || onExpandToggle(cardName, event);
  };
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId(`summary-card-${cardName}`)
  }, /*#__PURE__*/React__default.createElement(SummaryCardWrapper, _extends({}, rest, {
    ref: setRef,
    $minWidth: minWidth,
    $maxWidth: maxWidth,
    $width: width,
    $backgroundColor: backgroundColor,
    $padding: padding,
    $disabled: disabled,
    "aria-disabled": disabled
  }, disabled ? {
    inert: 'true'
  } : {}), title && /*#__PURE__*/React__default.createElement(SummaryCardHeader, {
    title: title,
    level: headerLevel,
    additionalContent: headerContent,
    isExpandable: !!expandedContent && !disabled,
    toggleButtonLabel: expandButtonLabel !== null && expandButtonLabel !== void 0 ? expandButtonLabel : formatMessage({
      id: 'expand_button_label'
    }, {
      name: title
    }),
    toggleButtonTooltip: formatMessage({
      id: 'expand_button_tooltip'
    }, {
      name: title
    }),
    toggleIcon: zoomIn,
    onToggleButtonClick: handleOnExpandToggle
  }), /*#__PURE__*/React__default.createElement(ContentWrapper, null, content)));
});
SummaryCard.displayName = 'SummaryCard';

export { SummaryCard as S };
//# sourceMappingURL=SummaryCard-DfB2sxkb.js.map
